import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { fetchAppSettings } from '../lib/app-settings';

interface AppSettingsCtx {
  settings: Map<string, any>;
  loading: boolean;
  isEnabled: (key: string) => boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AppSettingsCtx>({
  settings: new Map(), loading: true, isEnabled: () => false, refresh: async () => {},
});

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Map<string, any>>(new Map());
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const s = await fetchAppSettings();
      setSettings(s);
    } catch { /* ignore on initial load */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const value = useMemo<AppSettingsCtx>(() => ({
    settings,
    loading,
    isEnabled: (key: string) => settings.get(key) === true,
    refresh: load,
  }), [settings, loading]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAppSettings = () => useContext(Ctx);
